import React, { useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import { Colors } from "../../../configs";
import { pxToRem } from "../../../util";
import { AssignedConveyor } from "../../../interfaces";
import { useAppDispatch } from "../../../hooks";
import { getallRacksAction } from "../../../redux/actions/rackAction";

const tableHeaders = [
  "No.",
  "Date",
  "Time",
  "Cashier ID",
  "Bill ID",
  "Rack",
  "Location",
];

interface ConveyorInputHistoryTableProps {
  data: AssignedConveyor[];
  loading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

const ConveyorInputHistoryTable: React.FC<ConveyorInputHistoryTableProps> = ({
  data,
  loading = false,
  onLoadMore,
  hasMore = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current || !hasMore || loading) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        onLoadMore?.();
      }
    };

    const node = scrollRef.current;
    if (node) node.addEventListener("scroll", handleScroll);
    return () => {
      if (node) node.removeEventListener("scroll", handleScroll);
    };
  }, [onLoadMore, hasMore, loading]);

  useEffect(() => {
    dispatch(getallRacksAction());
  }, []);

  const displayCell = (v: unknown): React.ReactNode => {
    if (typeof v === "string") return v.replace(/^\s*slot\s*/i, "").trim();
    if (typeof v === "number") return v;
    if (v == null) return "";
    return String(v);
  };

  return (
    <TableContainer
      sx={{
        width: pxToRem(1630),
        height: pxToRem(600),
        borderRadius: pxToRem(12),
        overflow: "hidden",
      }}
    >
      <Box
        ref={scrollRef}
        sx={{
          maxHeight: pxToRem(600),
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": { width: "0rem" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ADADAD",
            border: "0.6rem solid transparent",
            backgroundClip: "padding-box",
            minHeight: "3rem",
          },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        }}
      >
        <Table
          stickyHeader
          sx={{ tableLayout: "fixed", borderSpacing: pxToRem(2) }}
        >
          <TableHead>
            <TableRow
              sx={{ backgroundColor: Colors.SECONDARY, height: pxToRem(41) }}
            >
              {tableHeaders.map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    fontSize: pxToRem(18),
                    fontWeight: "bold",
                    color: Colors.WHITE,
                    height: pxToRem(41),
                    lineHeight: pxToRem(41),
                    backgroundColor: Colors.SECONDARY,
                    padding: "0px",
                    textAlign: "center",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={tableHeaders.length}
                  sx={{
                    textAlign: "center",
                    padding: pxToRem(20),
                    fontSize: pxToRem(18),
                    color: Colors.MAIN_GRAY,
                    backgroundColor: Colors.ACCENT_GRAY,
                  }}
                >
                  No record available
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor: Colors.ACCENT_GRAY,
                    height: pxToRem(41),
                  }}
                >
                  {/* Columns are listed explicitly, in tableHeaders order. This used
                      to iterate Object.entries(row), which silently coupled the layout
                      to the API's JSON key order — adding a field shifted every cell. */}
                  {[
                    row.no,
                    row.date,
                    row.time,
                    row.cashierId,
                    row.billNumber,
                    row.conveyorId,
                    row.slotName,
                  ].map((cell, cellIndex, arr) => (
                      <TableCell
                        key={cellIndex}
                        sx={{
                          fontSize: pxToRem(18),
                          color: Colors.MAIN_GRAY,
                          height: pxToRem(41),
                          padding: "0px",
                          textAlign: "center",
                          borderBottomLeftRadius:
                            index === data.length - 1 && cellIndex === 0
                              ? pxToRem(5)
                              : 0,
                          borderBottomRightRadius:
                            index === data.length - 1 &&
                            cellIndex === arr.length - 1
                              ? pxToRem(5)
                              : 0,
                        }}
                      >
                        {displayCell(cell)}
                      </TableCell>
                    ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </TableContainer>
  );
};

export default ConveyorInputHistoryTable;
